import { Button, Image, Loader, ReactDropdown, ReactSwitch } from '@storybook';
import { FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';

import { ExportPdf } from '../export-pdf';
import { renderColor } from './util';
import { useSessionDetailsActions } from 'views/sessions/hooks/use-session-actions';
import { AssignRiskLevelActions, DownloadActions, IRiskLevelAction } from 'views/sessions/constants';
import { API_URL } from 'constant';
import {
	ComplexSelectedSession,
	ComplexSelectedSessionAssigneeIDState,
	ComplexSessionInfoState,
	ComplexSessionNodesState,
	OnboardingCipState,
	ConditionalSessionFlowState,
	FundDetailState,
	loginState,
	PreviousSessionDataState,
	SelectedSessionsIdState,
	SessionItemSelected,
	SessionState,
	userDataState,
} from 'global-stores';
import { useComplexSessionInfo, useOnboardingCip } from 'views/sessions/hooks';
import { ColorLegends } from '../color-legends';
import { InboxSignModal } from 'views/signed-doc-inbox/components/inbox-sign-modal';
import { useNotification, useNetwork } from 'hooks';
import { useSignedDocsInbox } from 'views/signed-doc-inbox/store/hooks';
import { REACT_APP_ARQ_SECURITIES_BUSINESSID, REACT_APP_LIQUIDITY_BUSINESSID, REACT_APP_MLC_BUSINESSID, REACT_APP_VCC_BUSINESSID } from 'envs';
import classNames from 'classnames';
import { AssignReviewerModal } from '../assign-reviewer-modal/assign-reviewer-modal';
import { AssignSelectedSessionState, dropDownSelectedReviewerState } from '../select-fund-modal';
import { useSessionAssign } from 'views/sessions/hooks/use-session-assign';
import { AssigneeSessionState, IsAssigneeSessionLoadingState } from 'views/sessions/store';
import { Assignee } from '../assign-dropdown/ReviewerDropdown';
import { LinkedEnvelopesState, PERMISSION_SCREEN, useUserRoles } from 'views';
import { ConfirmationModal, defaultPdfOptions, IPdfDownloadOption, PdfDownloadModal } from 'components';
interface IKycDetailsHeader {
	isTransactionPage?: boolean;
	handleBackModal?: () => void;
	sessionSelected: {
		name?: string;
		_id?: string;
		type?: string;
		representativeId?: string;
		nodes?: [{stepId: string, envelopeId: string}];
		businessId?: string;
		parentId?: boolean;
	};
	isComplexFlow: boolean;
	setIsComplexFlow: any;
	getLabel?: (val: string) => any;
	isFundView?: boolean;
	isShowTreeFlow?: boolean;
}



export const SessionDetailsHeader: FC<IKycDetailsHeader> = ({
	sessionSelected,
	handleBackModal,
	isTransactionPage,
	isComplexFlow,
	getLabel,
	setIsComplexFlow,
	isFundView = false,
	isShowTreeFlow = true,
}) => {
	const isComplexTab = useRecoilValue(ConditionalSessionFlowState);
	const complexSelectedSession = useRecoilValue(ComplexSelectedSession);
	const complexSelectedSessionAssigneeID = useRecoilValue(ComplexSelectedSessionAssigneeIDState);
	const sessionList = useRecoilValue(SessionState);
	const selectedIdState = useRecoilValue(SelectedSessionsIdState);
	const fundDetailList = useRecoilValue(FundDetailState);
	const setSessionItemSelected = useSetRecoilState(SessionItemSelected);
	const setComplexSessionNodes = useSetRecoilState(ComplexSessionNodesState);
	const setSelectedIdState = useSetRecoilState(SelectedSessionsIdState);
	const { complexSessionInfo } = useComplexSessionInfo();
	const previousSessionData = useRecoilValue(PreviousSessionDataState);
	const resetComplexSessionInfo = useResetRecoilState(ComplexSessionInfoState);
	const complexSessionInfoData = useRecoilValue(ComplexSessionInfoState);
	const { exportCsv, exportPdf } = useSessionDetailsActions();
	const { downloadRaw, downloadFormatted } = useOnboardingCip();
	const onboardingCipData = useRecoilValue(OnboardingCipState);
	const [showSignModal, setShowSignModal] = useState<boolean>(false);
	const [signLoader, setSignLoader] = useState<boolean>(false);
	const [sendSignLoader, setSendSignLoader] = useState<boolean>(false);
	const [sendSignPopup, setSendSignPopup] = useState<boolean>(false);
	const [signPrepareUrl, setSignPrepareUrl] = useState<{ env: string; url: string }>({
		env: '',
		url: '',
	});
	const [selectedRiskItem, setSelectedRiskItem] = useState<IRiskLevelAction>(	AssignRiskLevelActions[0] as IRiskLevelAction);
	const [currentRiskLevel, setCurrentRiskLevel] = useState<string>('');
	const { signDocInbox, getSignedDocsInbox } = useSignedDocsInbox();
	const { checkUserWritePermission } = useUserRoles();
	const { successNotification, errorNotification } = useNotification();
	const { resendSignDocInbox } = useSignedDocsInbox();
	const userDetail = useRecoilValue(loginState);
	const linkedEnvelopes = useRecoilValue(LinkedEnvelopesState);
	// Network hook for API calls
	const {	patch: updateActivity,} = useNetwork();

	// PDF Download Modal state
	const [isPdfModalVisible, setIsPdfModalVisible] = useState<boolean>(false);

	const setSelectedSession = useSetRecoilState(AssignSelectedSessionState);
	const [openAssignReviewer, setOpenAssignReviewer] = useState(false);
	const { AssignReviewer } = useSessionAssign();
	const sessionAssignee = useRecoilValue(AssigneeSessionState);
	const isAssigneeSessionLoading = useRecoilValue(IsAssigneeSessionLoadingState);
	const getDropDownSelectedReviewer = useRecoilValue(dropDownSelectedReviewerState);
	const loginStateData = useRecoilValue(userDataState);
	const SessionListFullStatus = useMemo(() => {
		if (isFundView) {
			return fundDetailList.find(
				value => value.sessionId === selectedIdState[0]
			)?.fullStatus;
		} else {
			return sessionList.find(value => value._id === selectedIdState[0])
				?.fullStatus;
		}
	}, [selectedIdState, sessionList, fundDetailList, isFundView]);

	const handleChangeDownloadAction = useCallback((event: any) => {
		const { value } = event ?? {};
		if (value === 'csv') {
			exportCsv(sessionSelected._id ?? '', sessionSelected._id ?? 'session');
		} else if (value === 'pdf') {
			setIsPdfModalVisible(true);
		} else if (value === 'cip-formatted') {
			// Pretty, print-ready PDF from the already-loaded CIP export.
			if (onboardingCipData?.sessionId) {
				downloadFormatted(onboardingCipData);
			} else {
				errorNotification('No tiered onboarding (CIP) data for this session');
			}
		} else if (value === 'cip-raw') {
			// Full provider dump (KYC/AML native payloads) — re-fetched with ?include=raw on demand.
			if (onboardingCipData?.sessionId) {
				downloadRaw(sessionSelected._id ?? '');
			} else {
				errorNotification('No tiered onboarding (CIP) data for this session');
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [onboardingCipData, sessionSelected._id]);

	// Risk Level Change Handler with API Integration
	const handleRiskLevelChange = useCallback(async (item: any) => {
		const { value, label } = item;
		
		// Check if the selected risk level is the same as current - if yes, don't call API
		if (value === currentRiskLevel) {
			return; // Don't make API call
		}
		// Prepare API data (same structure as activityLog.tsx)
		const activityData = {
			data: ``,
			type: 'comment',
			userName: loginStateData?.name,
			sendMail: false,
			requiredAttachment: false,
			recipientEmail: sessionSelected?.representativeId || '',
			bcc: [],
			cc: [],
			flow: isComplexTab ? 'complex' : 'linear',
			priorityRisk: value,
			previousPriorityRisk: currentRiskLevel || 'unassigned',
		};
		
		try {
			// Update UI immediately for better UX
			setSelectedRiskItem(item);
			
			// Make API call
			const response = await updateActivity(
				`${API_URL.ACTIVITIES}/${sessionSelected?._id}`,
				activityData
			);
			
			
			if (response) {
				setCurrentRiskLevel(value);
				successNotification(`Risk level changed to ${label}`);
				
				// Refresh session data to get updated activities
				if (isComplexTab && sessionSelected._id) {
					complexSessionInfo(sessionSelected._id);
				}
			} else {
				errorNotification('Failed to update risk level');
				// Revert UI on failure
				const previousItem = AssignRiskLevelActions.find(
					action => action.value === currentRiskLevel
				);
				if (previousItem) setSelectedRiskItem(previousItem);
			}
		} catch (error) {
			errorNotification('Failed to update risk level');
			// Revert UI on error
			const previousItem = AssignRiskLevelActions.find(
				action => action.value === currentRiskLevel
			);
			if (previousItem) setSelectedRiskItem(previousItem);
		}
		
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentRiskLevel, sessionSelected?._id, loginStateData, isComplexTab]);

	// Load initial risk level from session data or activities
	useEffect(() => {
		// First, try to get the risk level from activities (most recent priorityRisk from API)
		const activities = complexSessionInfoData?.activities || [];
		const latestRiskActivity = activities.find(
			(activity: any) => activity?.metadata?.priorityRisk
		);
		const riskLevelFromActivities = latestRiskActivity?.metadata?.priorityRisk;
		
		// Fallback to sessionSelected.priorityRisk if no risk level found in activities
		const sessionRiskLevel = riskLevelFromActivities || (sessionSelected as any)?.priorityRisk;		
		if (sessionRiskLevel && sessionRiskLevel !== 'unassigned') {
			setCurrentRiskLevel(sessionRiskLevel);
			
			const riskItem = AssignRiskLevelActions.find(
				action => action.value === sessionRiskLevel
			);
			if (riskItem) {
				setSelectedRiskItem(riskItem);
			}
		} else {
			// Reset to default if no risk level or unassigned
			setCurrentRiskLevel('');
			setSelectedRiskItem({ label: 'Assign Risk', value: '', color: 'var(--color-label-dark-100)', isVisible: false } as any);
		}
	}, [sessionSelected, complexSessionInfoData]);

	// Custom styles for Risk Level dropdown (same as action.tsx)
	// Material-3 themed control shared by the header dropdowns (Risk / Download).
	const riskLevelStyle = {
		control: (styles: { [key: string]: number | string }, state: any) => ({
			...styles,
			border: `1px solid ${
				state.isFocused ? 'var(--m3-primary)' : 'var(--m3-outline)'
			}`,
			backgroundColor: 'var(--m3-surface)',
			borderRadius: 10,
			color: 'var(--m3-on-surface)',
			boxShadow: state.isFocused ? '0 0 0 3px var(--m3-focus-ring)' : 'none',
			minHeight: 40,
			height: 40,
			width: 188,
			fontWeight: 500,
			fontSize: 13,
			transition: 'border-color .15s ease, box-shadow .15s ease',
			'&:hover': {
				cursor: 'pointer',
				borderColor: 'var(--m3-outline)',
			},
		}),
		placeholder: (styles: { [key: string]: any }) => ({
			...styles,
			color: 'var(--m3-on-surface-variant)',
		}),
		singleValue: (styles: { [key: string]: number | string }, { data }: any) => ({
			...styles,
			color: 'var(--m3-on-surface)',
			display: 'flex',
			alignItems: 'center',
			'&:before': data.value ? {
				// Only show dot if there's a value (not "Assign Risk")
				content: '""',
				display: 'inline-block',
				width: '8px',
				height: '8px',
				borderRadius: '50%',
				backgroundColor: data.color,  // Only dot is colored
				marginRight: '8px',
			} : {},
		}),
		menu: (styles: { [key: string]: any }) => ({
			...styles,
			zIndex: 9,
			borderRadius: 12,
			overflow: 'hidden',
			border: '1px solid var(--m3-outline-variant)',
			boxShadow: 'var(--m3-elevation-2)',
			backgroundColor: 'var(--m3-surface)',
		}),
		dropdownIndicator: (provided: any) => ({
			...provided,
			svg: {
				fill: 'var(--m3-on-surface-variant)',
			},
		}),
		indicatorSeparator: (provided: any) => ({
			...provided,
			backgroundColor: 'var(--m3-outline-variant)',
		}),
		option: (styles: { [key: string]: any }, { data, isFocused }: any) => {
			return {
				...styles,
				backgroundColor: isFocused
					? 'var(--m3-surface-container)'
					: 'var(--m3-surface)',
				color: 'var(--m3-on-surface)',
				cursor: 'pointer',
				display: 'flex',
				alignItems: 'center',
				fontSize: 13,
				'&:active': {
					backgroundColor: 'var(--m3-surface-container-high)',
				},
				'&:last-child': {
					borderBottom: 'none',
				},
				'&:before': {
					content: '""',
					display: 'inline-flex',
					alignItems: 'center',
					width: '8px',
					height: '8px',
					borderRadius: '50%',
					backgroundColor: data.color,  // Only dot is colored
					marginRight: '8px',
				},
			};
		},
	};

	// @paras: this back button handler is only for kyc-aml verification members screen
	const handleKycAmlBackButton = useCallback(() => {
		if (previousSessionData) {
			const sessionId = previousSessionData?._id;
			const allNodes = previousSessionData?.nodes?.map(el => {
				return {
					...el,
					visited: true,
					session: sessionId || '',
				};
			});
			resetComplexSessionInfo();
			setComplexSessionNodes(allNodes ?? []);
			complexSessionInfo(sessionId ?? '');
			setSessionItemSelected({
				...previousSessionData,
				type: 'complex',
				name: previousSessionData?.name ?? 'NaN',
				_id: sessionId || '',
			});
			setSelectedIdState([sessionId ?? '']);
		}
	}, [
		complexSessionInfo,
		previousSessionData,
		setComplexSessionNodes,
		setSelectedIdState,
		setSessionItemSelected,
		resetComplexSessionInfo,
	]);

	const signNodeData: any = useMemo(() => {
		return sessionSelected?.nodes?.find(el => el.stepId === 'signAgreement' && !!el?.envelopeId)
	}, [sessionSelected])

	const signBtnLabel = useMemo(() => {
		return signLoader ? (
			<div className="loader-wrapper">
				<Loader className="loader-white" dimension={20} type="loader" />
			</div>
		) : (
			signNodeData?.action?.status === 'completed' ? 'Signed' :'Sign/View'
		);
	}, [signLoader, signNodeData]);

	const sendSignBtnLabel = useMemo(() => {
		return sendSignLoader ? (
			<div className="loader-wrapper">
				<Loader className="loader-white" dimension={20} type="loader" />
			</div>
		) : (
			'Yes'
		);
	}, [sendSignLoader]);

	const handleSendForSign = useCallback(async (isOpen: boolean, value: boolean) => {
		if(value){
			setSendSignLoader(true);
			await resendSignDocInbox(signNodeData?.envelopeId, setSendSignLoader);
		}
		setSendSignPopup(isOpen);
	},[resendSignDocInbox, signNodeData?.envelopeId])

	// Handle PDF download with selected options
	const handlePdfDownload = useCallback(async (selectedOptions: string[]) => {
		try {
			if (selectedOptions.includes('all_combined')) {
				// Download everything as one combined PDF
				await exportPdf([sessionSelected._id], 'pdf');
			} else {
				const include: Record<string, boolean> = {};
				selectedOptions.forEach(option => {
					include[option] = true;
				});
				await exportPdf([sessionSelected._id], 'pdf', include);
			}
		} catch (error) {
			// Handle error silently - you can add error notification here if needed
		}
	}, [exportPdf, sessionSelected._id]);

	// Close PDF modal handler
	const handleClosePdfModal = useCallback(() => {
		setIsPdfModalVisible(false);
	}, []);

	const handleSignDoc = useCallback(async () => {
		setSignLoader(true);
		const response = await signDocInbox(
			{
				envelopeId: signNodeData?.envelopeId ?? '',
				recipientEmail: userDetail.email,
			},
			setSignLoader
		);
		const { url, env } = response ?? {};
		if (url && env) {
			setSignPrepareUrl({ url, env });
			setShowSignModal(true);
		}
	}, [signDocInbox, signNodeData, userDetail.email]);

	const handleCloseIframe = useCallback(async () => {
		setShowSignModal(false);
		setSignPrepareUrl({ env: '', url: '' });
	}, []);

	const handleSubmitModal = useCallback(() => {
		handleCloseIframe();
		setTimeout(() => {
			successNotification('Document Signed Successfully.');
			getSignedDocsInbox(false);
		}, 1000);
	}, [getSignedDocsInbox, handleCloseIframe, successNotification]);

	const checkSessionCompleted = useMemo(() => {
		if(complexSelectedSession?.status !== "completed" ){
			return false;
		}
		const filterdNodes = complexSelectedSession.nodes.filter((el: any) => el.visited && el.stepId !== 'authentication' && el.stepId !== 'signAgreement');
		return filterdNodes.every((el: any) => el.action.status === 'completed')
	}, [complexSelectedSession])

	useEffect(() => {
		// `== null` ON PURPOSE -- it matches undefined as well as null.
		//
		// A session that has NEVER been assigned has no `assignee` key in its
		// metadata at all, so this id comes through as `undefined`
		// (use-complex-session-details.ts reads data.metadata?.assignee). With a
		// strict `=== null` that failed both halves of this guard, AssignReviewer()
		// was never called, /assignee-users was never fetched, and
		// IsAssigneeSessionLoadingState -- which defaults to TRUE and is only
		// cleared inside AssignReviewer's then/catch -- stayed true forever. The
		// control then renders its Loader indefinitely: the reviewer sees a
		// dropdown that spins and never opens.
		//
		// It looked intermittent because the table's filter component calls
		// AssignReviewer() unconditionally on mount, so any route that passed
		// through it cleared the flag first -- while closing the details panel
		// resets it to true again. Straight to /sessions?search=... and clicking a
		// never-assigned session reproduced it every time.
		if (complexSelectedSessionAssigneeID?.length > 0 || complexSelectedSessionAssigneeID == null) {
			AssignReviewer();
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ complexSelectedSessionAssigneeID]);

	const reviewers = useMemo(
		() =>
			sessionAssignee.map((assignee: Assignee) => ({
				id: `${assignee?._id}`,
				name: assignee?._id === loginStateData.id
					? 'Self assigned'
					: `${assignee.firstName} ${assignee.lastName}`,
				image: `${assignee.image}`,
			})),
		[loginStateData.id, sessionAssignee]
	);

	const getAssignedReviewer = useMemo(
		() =>
			reviewers.filter(
				(reviewer: { id: string }) =>
					reviewer.id === complexSelectedSessionAssigneeID
			),
		[reviewers, complexSelectedSessionAssigneeID]
	);

	const showUserImage = useMemo(() => {
		const image = getAssignedReviewer[0]?.image;
		const selectedReviewerImage = getDropDownSelectedReviewer
			.map(reviewer => reviewer.image)
			.join(', ');
		return selectedReviewerImage || image;

	}, [getAssignedReviewer, getDropDownSelectedReviewer]);
	

	const showUserName = useMemo(() => {
		const selectedReviewerNames = getDropDownSelectedReviewer
			.map(reviewer => reviewer.name)
			.join(', ');
		const reviewerName = selectedReviewerNames || getAssignedReviewer[0]?.name;
		if (reviewerName && reviewerName!=='Unassigned') {
			return reviewerName
		} else {
			return 'Assign Reviewer';
		}
	}, [getDropDownSelectedReviewer, getAssignedReviewer]);

	const classes = useCallback(
		(check: boolean) =>
			classNames('ri-arrow-down-s-line', {
				'filter-popup__opened': check,
				'filter-popup__closed': !check,
			}),
		[]
	);

	const isUserPermissionWrite = useMemo(
		() => checkUserWritePermission(PERMISSION_SCREEN.Sessions),
		[checkUserWritePermission]
	);
	
	const handleClickAssignReviewer = useCallback(() => {
		if (isUserPermissionWrite) {
			setOpenAssignReviewer(true);
			setSelectedSession([sessionSelected]);
		}
		return;
	}, [isUserPermissionWrite, sessionSelected, setSelectedSession]);

	const renderAssineeImg = useCallback(() => {
		if(showUserImage === 'null') {
			return (
				<div className="unassign-defult-image">
					<i className="ri-user-line" />
				</div>
			);
		} else {
			return <Image url={showUserImage} className="reviewer-image" />;
		}
	},[showUserImage])

	const pdfDownloadOptions: IPdfDownloadOption[] = useMemo(() => {
		// Essential options that should always be included
		const essentialOptions = ['sessionInformation', 'comments', 'linkedEsign'];
		
		// Filter options that either are essential OR have matching nodes
		const availableOptions = defaultPdfOptions.filter((option) => {
			// Always include essential options
			if (essentialOptions.includes(option.key)) {
				if(option.key === 'linkedEsign') {
					return !!linkedEnvelopes.length && linkedEnvelopes.some((envelope) => envelope.status === 'completed');
				}
				return true;
			}
			
			// For other options, check if they have matching nodes
			const hasMatchingNode = sessionSelected?.nodes?.some((node) => node.stepId === option.key);
			return hasMatchingNode;
		});
		
		return availableOptions;
	}, [sessionSelected?.nodes, linkedEnvelopes]);

	// Contact + session identifiers surfaced in the header (moved out of the old
	// Basic Info card). Email / phone come from the CIP applicant; id from the row.
	const headerApplicant = (onboardingCipData as any)?.applicant ?? {};
	const headerEmail: string = headerApplicant?.email ?? '';
	const headerPhone: string = headerApplicant?.phone
		? `${headerApplicant?.countryCode ?? ''} ${headerApplicant?.phone}`.trim()
		: '';
	const headerSessionId: string =
		(sessionSelected?._id as string) || (selectedIdState?.[0] as string) || '';

	return (
		<div className="kyc--Details--header--sticky">
			<div
				className={`kycDetails--header--export-pdf kycDetails--header--sticky ${
					isTransactionPage ? 'kyc-details-transaction-top-header' : ''
				}`}
			>
				<div className="kycDetails--header_detail kycDetails--header--sticky align__back_icon">
					{isTransactionPage && (
						<div onClick={handleBackModal} className="arrow__left_icon_main">
							<i className="ri-arrow-left-line fa-lg" />
						</div>
					)}
					{/* @paras: this back button is only for kyc-aml verification members screen */}
					{sessionSelected?.representativeId?.length && (
						<div
							onClick={handleKycAmlBackButton}
							className="arrow__left_icon_main"
						>
							<i className="ri-arrow-left-line fa-lg" />
						</div>
					)}

					<Fragment>
						<div className="kycUserTitle">
							<div className="kycUserName">
								<span>{sessionSelected.name}</span>
							</div>
							{(headerEmail || headerPhone || headerSessionId) && (
								<div className="kycUserMeta">
									{headerEmail && (
										<span className="kycUserMeta__item" title={headerEmail}>
											<i className="ri-mail-line" />
											{headerEmail}
										</span>
									)}
									{headerPhone && (
										<span className="kycUserMeta__item">
											<i className="ri-phone-line" />
											{headerPhone}
										</span>
									)}
									{headerSessionId && (
										<span
											className="kycUserMeta__item kycUserMeta__id"
											title={headerSessionId}
										>
											<i className="ri-fingerprint-line" />
											{headerSessionId}
										</span>
									)}
								</div>
							)}
						</div>
						{!isComplexTab && !isTransactionPage && (
							<div className="Session-show-status">
								{Object.keys(SessionListFullStatus ?? {})?.map(keys => {
									const colors: any = renderColor(
										keys,
										SessionListFullStatus?.[keys as any] as string
									);

									return (
										<div
											key={'status-circle' + keys}
											className="Session-show-status-contain"
										>
											<div
												className="Session-show-status-circle"
												style={{ backgroundColor: colors.color }}
											/>
											<div>
												{getLabel && getLabel(keys)}
												{colors.status?.length > 0 && ` (${colors.status})`}
											</div>
										</div>
									);
								})}
							</div>
						)}
					</Fragment>
				</div>
				<div className="kycDetails--header--rightCluster">
				<div className="kycDetails--header--toggleContainer">
					{
						<div className="kycDetails--header-btn-container">
							{isComplexTab && isShowTreeFlow && (
								<div className="kycDetails--header--toggleContainer_togglebtn">
									<div className="kycDetails--header--toggleContainer_row">
										<div className="kycDetails--header--toggleContainer_text">
											Tree Flow
										</div>
										<ReactSwitch
											id={''}
											onColor="#33b87a"
											checked={isComplexFlow}
											handleChange={() =>
												setIsComplexFlow((prev: boolean) => !prev)
											}
										/>
									</div>
								</div>
							)}
							{/* Risk Level Dropdown with same UI as action.tsx */}

							<ReactDropdown
								handleChangeSelect={handleRiskLevelChange}
								options={AssignRiskLevelActions}
								value={selectedRiskItem}
								defaultValue={selectedRiskItem}
								createStyle={riskLevelStyle}
								controlStyle={{ width: 188 }}
								Isdisable={!isUserPermissionWrite}
							/>
							{sessionSelected.type === 'complex' ? (
								<>
									{
										(userDetail.business ===
											REACT_APP_LIQUIDITY_BUSINESSID ||
											userDetail.business === REACT_APP_MLC_BUSINESSID || 
											userDetail.business === REACT_APP_VCC_BUSINESSID) &&
										!!signNodeData &&
										(
											<Button
												label={'Send For Signing'}
												handleClick={() => setSendSignPopup(true)}
												type="button__filled--primary sign-btn"
											/>
										)}

									{!!signNodeData &&
										checkSessionCompleted &&
										userDetail.business ===
										REACT_APP_ARQ_SECURITIES_BUSINESSID &&
										(signNodeData?.action?.status === 'processing' ||
											signNodeData?.action?.status === 'completed') && (
											<Button
												label={signBtnLabel}
												handleClick={() => handleSignDoc()}
												disabled={
													signLoader ||
													signNodeData?.action?.status === 'completed'
												}
												type="button__filled--primary sign-btn"
											/>
										)}
									{!sessionSelected?.parentId && <div className="assigned-user-dropdown">
										<div
											className={isUserPermissionWrite ? "assigned-user-dropdown-header" : "assigned-user-dropdown-header-disable"}
											onClick={handleClickAssignReviewer}
										>
											<div className="assignee-content">
												{isAssigneeSessionLoading ? (
													<div className="assignee-session-loading">
														<Loader type="loader" dimension={20} />
													</div>
												) : (
													<>
														{showUserName !== 'Assign Reviewer' && renderAssineeImg()}
														<span
															className={
																showUserName !== 'Assign Reviewer'
																	? 'assignee-username'
																	: ''
															}
														>
															{showUserName}
														</span>
													</>
												)}
											</div>

											<i className={classes(openAssignReviewer && !isAssigneeSessionLoading)}></i>
										</div>

										{openAssignReviewer && !isAssigneeSessionLoading && (
											<AssignReviewerModal
												isOpen={openAssignReviewer}
												handleClose={() => {
													setOpenAssignReviewer(false);
												}}
												isCurrentUser={true}
											/>
										)}
									</div>}
									<ReactDropdown
										handleChangeSelect={handleChangeDownloadAction}
										options={DownloadActions}
										defaultValue={{ label: 'Download' }}
										createStyle={riskLevelStyle}
										controlStyle={{ width: 188 }}
									/>
								</>
							) : (
								<ExportPdf
									sessionIDs={[sessionSelected._id]}
									type={'pdf'}
									label={'Download'}
								/>
							)}
						</div>
					}
				</div>
			<ColorLegends title={'Status & Flow Legends:'} />
				</div>
			</div>
			
			{/* Risk Warning Banner - Shows when risk level is assigned */}
			{currentRiskLevel && currentRiskLevel !== 'unassigned' && (
				<div className="risk-warning-banner">
					<i className="ri-alarm-warning-line warning-icon"></i>
					<div className='risk-warning-banner-content'>
						<div className='risk-warning-banner-content-title'>
							A potential risk associated with this user has been identified.
						</div>
					</div>
				</div>
			)}
			
			{showSignModal && (
				<InboxSignModal
					modalVisibility={showSignModal}
					handleCloseIframe={handleCloseIframe}
					prepareUrl={signPrepareUrl}
					handleSubmitModal={handleSubmitModal}
				/>
			)}
			{sendSignPopup && <ConfirmationModal
				visible={sendSignPopup}
				title={'Send For Signing'}
				description={'This button will send the final signed agreement to Coleman for signing. Have you reviewed everything and want to proceed?'}
				handleModal={handleSendForSign}
				label={sendSignBtnLabel}
				primaryBtnClass={'send_sign_btn'}
				descClass={'send_sign_desc'}
				disabled={sendSignLoader}
			/>}
			
			{/* PDF Download Modal */}
			<PdfDownloadModal
				visible={isPdfModalVisible}
				options={pdfDownloadOptions}
				handleClose={handleClosePdfModal}
				onDownload={handlePdfDownload}
				title="Download Options"
			/>
		</div>
	);
};
